import { z } from 'zod';
import { InternalNetworkAgentStatusSchema } from './internal-network.schema';

export const TunnelSubnetRouterArtifactsSchema = z.object({
  composeYaml: z.string(),
  deploymentCommand: z.string(),
  advertisedRoutes: z.array(z.string()),
  tailscaleImageReference: z.string(),
});

export type TunnelSubnetRouterArtifactsDto = z.infer<
  typeof TunnelSubnetRouterArtifactsSchema
>;

export const CreateTunnelEnrollmentBodySchema = z.object({
  advertisedRoutes: z.array(z.string()).optional(),
});

export type CreateTunnelEnrollmentBodyDto = z.infer<
  typeof CreateTunnelEnrollmentBodySchema
>;

export const CreateTunnelEnrollmentResponseSchema = z.object({
  enrollmentId: z.string(),
  aclTag: z.string(),
  advertisedRoutes: z.array(z.string()),
  artifacts: TunnelSubnetRouterArtifactsSchema,
  apiKey: z.string().optional(),
});

export type CreateTunnelEnrollmentResponseDto = z.infer<
  typeof CreateTunnelEnrollmentResponseSchema
>;

export const TunnelEnrollmentSchema = z.object({
  id: z.string(),
  networkId: z.string(),
  status: InternalNetworkAgentStatusSchema,
  aclTag: z.string().nullable(),
  advertisedRoutes: z.array(z.string()),
  approvedRoutes: z.array(z.string()),
  routesApproved: z.boolean(),
  lastSeenAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type TunnelEnrollmentDto = z.infer<typeof TunnelEnrollmentSchema>;

export const TunnelEnrollmentListResponseSchema = z.object({
  data: z.array(TunnelEnrollmentSchema),
  total: z.number(),
});

export type TunnelEnrollmentListResponseDto = z.infer<
  typeof TunnelEnrollmentListResponseSchema
>;

export const LaunchTunnelEngineBodySchema = z.object({
  operationId: z.string(),
});

export type LaunchTunnelEngineBodyDto = z.infer<
  typeof LaunchTunnelEngineBodySchema
>;

export const TunnelEngineSessionSchema = z.object({
  operationId: z.string(),
  namespace: z.string(),
  podName: z.string(),
  engineTag: z.string(),
});

export type TunnelEngineSessionDto = z.infer<typeof TunnelEngineSessionSchema>;

export const TerminateTunnelEngineResponseSchema = z.object({
  operationId: z.string(),
  terminated: z.boolean(),
});

export type TerminateTunnelEngineResponseDto = z.infer<
  typeof TerminateTunnelEngineResponseSchema
>;

export const TunnelJumpOsSchema = z.enum(['linux', 'windows']);

export const DeployAndLinkHostBodySchema = z.object({
  operationId: z.string(),
  targetHost: z.string().min(1),
  os: TunnelJumpOsSchema,
  username: z.string().min(1).optional(),
  password: z.string().min(1).optional(),
  parentDisplayId: z.number().int().positive().optional(),
});

export type DeployAndLinkHostBodyDto = z.infer<
  typeof DeployAndLinkHostBodySchema
>;

export const LinkHostBodySchema = z.object({
  operationId: z.string(),
  targetHost: z.string().min(1),
  p2p: z.enum(['tcp', 'smb']),
  parentDisplayId: z.number().int().positive().optional(),
  pipeName: z.string().min(1).optional(),
  tcpPort: z.number().int().min(1).max(65535).optional(),
});

export type LinkHostBodyDto = z.infer<typeof LinkHostBodySchema>;

export const UnlinkHostBodySchema = z.object({
  operationId: z.string(),
  displayId: z.number().int().positive(),
  parentDisplayId: z.number().int().positive().optional(),
});

export type UnlinkHostBodyDto = z.infer<typeof UnlinkHostBodySchema>;

export const TunnelJumpResultSchema = z.object({
  networkId: z.string(),
  operationId: z.string(),
  targetHost: z.string(),
  parentDisplayId: z.number().nullable(),
  childDisplayId: z.number().nullable(),
  hopDepth: z.number().int().nonnegative(),
  p2pProfile: z.enum(['tcp', 'smb']),
  status: z.enum(['linked', 'deployed_and_linked', 'unlinked', 'failed']),
  message: z.string().optional(),
});

export type TunnelJumpResultDto = z.infer<typeof TunnelJumpResultSchema>;

export const TunnelMeshHostSchema = z.object({
  displayId: z.number().int().positive(),
  host: z.string(),
  hostAgentLabel: z.enum(['apollo', 'poseidon']),
  parentDisplayId: z.number().int().positive().nullable(),
  hopDepth: z.number().int().nonnegative().nullable(),
  lastSeenAt: z.string(),
});

export type TunnelMeshHostDto = z.infer<typeof TunnelMeshHostSchema>;

export const TunnelMeshStatusSchema = z.object({
  engineDisplayId: z.number().int().positive().nullable(),
  sensorDisplayId: z.number().int().positive().nullable(),
  hostAgents: z.array(TunnelMeshHostSchema),
});

export type TunnelMeshStatusDto = z.infer<typeof TunnelMeshStatusSchema>;
